import { useId } from 'react'
import { useAppStore } from '@core/store'
import { dataCurtaPt, progressoDoGrupo } from './clashReconciliacao'
import {
  ESPECIALIDADES,
  listaParaTexto,
  ORIGEM_LABELS,
  PRIORITIES,
  PRIORITY_LABELS,
  sugestoesDeEspecialidade,
  textoParaLista,
  type ClashPriority,
  type ClashTriageEntry,
  type ClashTriagePatch,
} from './clashTriage'

// ---------------------------------------------------------------------------
// Campos da coordenacao de um conflito: prioridade, especialidade
// responsavel, a quem comunicar, recomendacao, prazo, etiquetas e nota.
// O mesmo editor na lista (linha aberta) e no conflito em foco.
//
// Os selectores gravam ao mudar; os campos de texto gravam ao sair do
// campo (a triagem limpa os espacos das pontas, e gravar a cada tecla
// comia o espaco que se acabou de escrever). As listas (a comunicar,
// etiquetas) escrevem-se separadas por virgulas.
// ---------------------------------------------------------------------------

const CAMPO =
  'w-full rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-1 py-0.5 text-[11px]'

export interface ClashTriageEditorProps {
  entry: ClashTriageEntry
  /** Nomes dos modelos abertos, para as sugestões. */
  modelos: readonly string[]
  onPatch: (patch: ClashTriagePatch) => void
}

export default function ClashTriageEditor({ entry, modelos, onPatch }: ClashTriageEditorProps) {
  const id = useId()
  const listaEsp = `${id}-esp`
  const sugestoes = sugestoesDeEspecialidade(modelos)
  const triagem = useAppStore((s) => s.clashTriage)
  const progresso = entry.group ? progressoDoGrupo(triagem, entry.group) : null
  const eventos = entry.eventos ?? []
  return (
    <div className="flex flex-col gap-1 text-[11px]">
      <datalist id={listaEsp}>
        {sugestoes.map((s) => (
          <option key={s} value={s}>
            {ESPECIALIDADES.find((e) => e.sigla === s)?.nome ?? s}
          </option>
        ))}
      </datalist>
      <div className="grid grid-cols-2 gap-1">
        <label className="flex flex-col gap-0.5">
          <span className="text-[var(--color-text-dim)]">Prioridade</span>
          <select
            value={entry.priority ?? ''}
            onChange={(e) =>
              onPatch({ priority: (e.target.value || undefined) as ClashPriority | undefined })
            }
            className={CAMPO}
          >
            <option value="">sem prioridade</option>
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {PRIORITY_LABELS[p]}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="text-[var(--color-text-dim)]">Prazo</span>
          <input
            type="date"
            value={entry.dueDate ?? ''}
            onChange={(e) => onPatch({ dueDate: e.target.value || undefined })}
            className={CAMPO}
          />
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="text-[var(--color-text-dim)]">Especialidade responsável</span>
          <input
            list={listaEsp}
            defaultValue={entry.assignedTo ?? ''}
            key={`resp-${entry.assignedTo ?? ''}`}
            onBlur={(e) => onPatch({ assignedTo: e.target.value })}
            placeholder="ARQ, EST, MAC, HID, EEL"
            className={CAMPO}
          />
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="text-[var(--color-text-dim)]">A comunicar a</span>
          <input
            list={listaEsp}
            defaultValue={listaParaTexto(entry.notify)}
            key={`notify-${listaParaTexto(entry.notify)}`}
            onBlur={(e) => onPatch({ notify: textoParaLista(e.target.value) })}
            placeholder="separadas por vírgulas"
            className={CAMPO}
          />
        </label>
      </div>
      <label className="flex flex-col gap-0.5">
        <span className="text-[var(--color-text-dim)]">Recomendação de resolução</span>
        <textarea
          defaultValue={entry.recommendation ?? ''}
          key={`rec-${entry.recommendation ?? ''}`}
          onBlur={(e) => onPatch({ recommendation: e.target.value })}
          rows={2}
          className={CAMPO}
        />
      </label>
      <label className="flex flex-col gap-0.5">
        <span className="text-[var(--color-text-dim)]">Etiquetas</span>
        <input
          defaultValue={listaParaTexto(entry.tags)}
          key={`tags-${listaParaTexto(entry.tags)}`}
          onBlur={(e) => onPatch({ tags: textoParaLista(e.target.value) })}
          placeholder="separadas por vírgulas"
          className={CAMPO}
        />
      </label>
      <label className="flex flex-col gap-0.5">
        <span className="text-[var(--color-text-dim)]">Nota</span>
        <textarea
          value={entry.note}
          onChange={(e) => onPatch({ note: e.target.value })}
          rows={2}
          placeholder="Fica guardada com a triagem"
          className={CAMPO}
        />
      </label>
      {entry.group && (
        <p className="text-[10px] text-[var(--color-text-dim)]">
          Grupo {entry.group}
          {entry.groupTitle ? `: ${entry.groupTitle}` : ''}
          {progresso && progresso.total > 1
            ? ` (${progresso.resolvidos} de ${progresso.total} resolvidos)`
            : ''}
        </p>
      )}
      {entry.origem && (
        <p className="text-[10px] text-[var(--color-text-dim)]">
          Parecer: {ORIGEM_LABELS[entry.origem]}
          {entry.regra ? ` (regra ${entry.regra})` : ''}
          {entry.tipoConflito ? ` · ${entry.tipoConflito}` : ''}
          {entry.nivel ? ` · nível ${entry.nivel}` : ''}
          {entry.grupoFixo ? ' · grupo fixado à mão' : ''}
        </p>
      )}
      {entry.porVerificar && (
        <p className="text-[10px] text-[var(--color-warn-text)]">
          Por verificar: {entry.porVerificar}.
        </p>
      )}
      {eventos.length > 0 && (
        <ul className="flex flex-col gap-0.5 text-[10px] text-[var(--color-text-dim)]">
          {eventos.slice(-5).map((ev) => (
            <li key={`${ev.data}|${ev.tipo}`}>
              {dataCurtaPt(ev.data)}: {ev.texto}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
